import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { ObservableCursor } from 'meteor-rxjs';
import {
  BehaviorSubject,
  combineLatest,
  map,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Customer } from 'server/shared/models/customer';
import { getUniqueArrayItems } from '../../../helpers/getUniqueArrayItems';
@Component({
  selector: 'app-customer-multi-select',
  templateUrl: './customer-multi-select.component.html',
  styleUrls: ['./customer-multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CustomerMultiSelectComponent,
      multi: true,
    },
  ],
})
export class CustomerMultiSelectComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  private destroyed$ = new Subject<void>();

  searchTermControl = new FormControl('');

  @ViewChild('searchTermInputElement')
  searchTermInputElement?: ElementRef<HTMLInputElement>;

  selectedCustomerIds$ = new BehaviorSubject<string[]>([]);
  selectableCustomers$: ObservableCursor<Customer> = Customers.find();

  filteredCustomers$ = combineLatest([
    this.searchTermControl.valueChanges.pipe(
      startWith(this.searchTermControl.value),
      takeUntil(this.destroyed$),
    ),
    this.selectableCustomers$.pipe(takeUntil(this.destroyed$)),
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([searchTerm, customers]) =>
      customers.filter((customer) =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    ),
  );
  // deferred from selectedCustomerIds
  selectedCustomers$ = combineLatest([
    this.selectedCustomerIds$.pipe(takeUntil(this.destroyed$)),
    this.selectableCustomers$.pipe(takeUntil(this.destroyed$)),
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([selectedCustomerIds, customers]) =>
      customers.filter((customer) =>
        selectedCustomerIds.includes(customer._id),
      ),
    ),
  );

  private isDisabledSubject$ = new BehaviorSubject<boolean>(false);
  isDisabled$ = this.isDisabledSubject$.asObservable();

  onChange: any = (selectedCustomerIds: string[]) => {};
  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  onTouch: any = () => {};
  registerOnTouched(onTouched: Function) {
    this.onTouch = onTouched;
  }

  constructor() {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  emitChange() {
    this.onChange(this.selectedCustomerIds$.value);
  }

  writeValue(value: string[]) {
    this.selectedCustomerIds$.next(value);
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabledSubject$.next(isDisabled);
    if (isDisabled) {
      this.searchTermControl.disable();
    } else {
      this.searchTermControl.enable();
    }
  }

  removeSelectedCustomer(customerId: string) {
    this.selectedCustomerIds$.next(
      this.selectedCustomerIds$.value.filter((id) => id !== customerId),
    );
    this.emitChange();
  }

  addSelectedCustomer(customerId: string) {
    this.selectedCustomerIds$.next(
      getUniqueArrayItems([...this.selectedCustomerIds$.value, customerId]),
    );
    this.emitChange();
  }

  matInputSelect(
    $event: MatAutocompleteSelectedEvent,
    autoComplete: MatAutocompleteTrigger,
  ) {
    this.addSelectedCustomer($event.option.value);
    this.searchTermControl.reset('');
    // somehow resetting the autocomplete input value does not reset the
    // actual value in html, so we do it manually here
    if (this.searchTermInputElement?.nativeElement) {
      this.searchTermInputElement.nativeElement.value = '';
    }
    // re-open panel
    setTimeout(() => {
      autoComplete?.openPanel();
    });
  }
}
